import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { 
  Coffee, Wine, UtensilsCrossed, Cake, Moon,
  RefreshCw, Save, Clock, Star, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { SortableList } from './SortableList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  manana: Coffee,
  vermut: Wine,
  comida: UtensilsCrossed,
  tarde: Cake,
  noche: Moon
};

const SLOT_NAMES = {
  manana: 'Mañana',
  vermut: 'Vermut',
  comida: 'Comida',
  tarde: 'Tarde',
  noche: 'Noche'
};

const SLOT_COLORS = {
  manana: 'bg-amber-500',
  vermut: 'bg-rose-500',
  comida: 'bg-orange-500',
  tarde: 'bg-teal-500',
  noche: 'bg-violet-500'
};

const MomentoConfigPanel = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSlot, setExpandedSlot] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [editHours, setEditHours] = useState({});

  const fetchConfig = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/momento/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        // Init hours edit state
        const hours = {};
        data.slots?.forEach(s => {
          hours[s.slot_id] = { start_hour: s.start_hour, end_hour: s.end_hour };
        });
        setEditHours(hours);
      }
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchExperiences = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setExperiences(data.filter(p => p.product_type === 'experience' && p.is_active !== false));
      }
    } catch (error) {
      console.error('Error fetching experiences:', error);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchExperiences();
  }, [fetchConfig, fetchExperiences]);

  const linkExperience = async (slotId, productId) => {
    const token = localStorage.getItem('admin_token');
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/momento/slots/${slotId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ experience_product_id: productId || null })
      });
      if (response.ok) {
        toast.success(productId ? 'Experiencia vinculada' : 'Experiencia desvinculada');
        fetchConfig();
      }
    } catch (error) {
      toast.error('Error al vincular experiencia');
    } finally {
      setSaving(false);
    }
  };

  const saveSlotHours = async (slotId) => {
    const token = localStorage.getItem('admin_token');
    const hours = editHours[slotId];
    if (!hours) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/momento/slots/${slotId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_hour: parseInt(hours.start_hour), end_hour: parseInt(hours.end_hour) })
      });
      if (response.ok) {
        toast.success('Horario actualizado');
        fetchConfig();
      }
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleSlotActive = async (slotId, currentActive) => {
    const token = localStorage.getItem('admin_token');
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/momento/slots/${slotId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentActive })
      });
      if (response.ok) {
        toast.success(!currentActive ? 'Franja activada' : 'Franja desactivada');
        fetchConfig();
      }
    } catch (error) {
      toast.error('Error');
    } finally {
      setSaving(false);
    }
  };

  const getExperience = (productId) => experiences.find(e => e.id === productId);

  const handleReorderSlots = async (newItems) => {
    // Optimistic update
    setConfig(prev => ({ ...prev, slots: newItems.map(item => item._slot) }));
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${API_URL}/api/admin/momento/slots/reorder/batch`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ order: newItems.map(item => item.id) })
      });
      toast.success('Orden de franjas guardado');
    } catch (e) {
      toast.error('Error al reordenar');
      fetchConfig();
    }
  };

  // Wrap slots with `id` property for SortableList (dnd-kit needs `id`)
  const sortableSlots = (config?.slots || []).map(slot => ({
    id: slot.slot_id,
    _slot: slot,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-stone-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="momento-config-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Experiencias por Franja</h2>
          <p className="text-sm text-gray-500">Vincula una experiencia/pack a cada franja horaria. El banner mostrará la info del pack.</p>
        </div>
        <Button onClick={fetchConfig} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" /> Actualizar
        </Button>
      </div>

      {/* Slots — drag & drop reorderable */}
      <div className="space-y-3">
        <SortableList
          items={sortableSlots}
          onReorder={handleReorderSlots}
          renderItem={(item) => {
            const slot = item._slot;
            const Icon = ICONS[slot.slot_id] || Coffee;
            const isExpanded = expandedSlot === slot.slot_id;
            const linkedExp = slot.experience_product_id ? getExperience(slot.experience_product_id) : null;
            const hours = editHours[slot.slot_id] || {};

            return (
              <Card 
                className={`overflow-hidden transition-all ${!slot.is_active ? 'opacity-50' : ''} ${isExpanded ? 'ring-2 ring-amber-400' : ''}`}
                data-testid={`slot-card-${slot.slot_id}`}
              >
                {/* Slot Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedSlot(isExpanded ? null : slot.slot_id)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${SLOT_COLORS[slot.slot_id]} rounded-xl flex items-center justify-center`}>
                      <Icon className="text-white" size={20} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{SLOT_NAMES[slot.slot_id]}</span>
                        <Badge variant="outline" className="text-xs font-mono">
                          {slot.start_hour}:00 — {slot.end_hour}:00
                        </Badge>
                        {!slot.is_active && <Badge variant="secondary" className="text-xs">Off</Badge>}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {linkedExp 
                          ? <span className="text-amber-700 font-medium">{linkedExp.name?.es} — {linkedExp.price?.toFixed(2)}€</span>
                          : <span className="text-gray-400">Sin experiencia vinculada</span>
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {linkedExp?.images?.[0] && (
                      <img src={linkedExp.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    )}
                    {isExpanded ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <CardContent className="border-t bg-gray-50/50 space-y-5 pt-5">
                    {/* Active Toggle */}
                    <div className="flex items-center justify-between">
                      <Label className="font-medium">Franja activa</Label>
                      <Switch 
                        checked={slot.is_active !== false} 
                        onCheckedChange={() => toggleSlotActive(slot.slot_id, slot.is_active !== false)}
                        disabled={saving}
                      />
                    </div>

                    {/* Hours */}
                    <div>
                      <Label className="font-medium mb-2 block">Horario de la franja</Label>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <Label className="text-xs text-gray-500">Inicio</Label>
                          <Input 
                            type="number" min="0" max="23" 
                            value={hours.start_hour ?? ''} 
                            onChange={(e) => setEditHours(h => ({...h, [slot.slot_id]: {...h[slot.slot_id], start_hour: e.target.value}}))}
                            className="font-mono"
                          />
                        </div>
                        <span className="text-gray-400 pt-5">—</span>
                        <div className="flex-1">
                          <Label className="text-xs text-gray-500">Fin</Label>
                          <Input 
                            type="number" min="1" max="24" 
                            value={hours.end_hour ?? ''} 
                            onChange={(e) => setEditHours(h => ({...h, [slot.slot_id]: {...h[slot.slot_id], end_hour: e.target.value}}))}
                            className="font-mono"
                          />
                        </div>
                        <Button 
                          onClick={() => saveSlotHours(slot.slot_id)} 
                          disabled={saving} 
                          size="sm" 
                          className="mt-5 bg-amber-500 hover:bg-amber-600"
                        >
                          <Save size={14} />
                        </Button>
                      </div>
                    </div>

                    {/* Experience Selector */}
                    <div>
                      <Label className="font-medium mb-2 flex items-center gap-2">
                        <Star size={16} className="text-amber-500" />
                        Experiencia / Pack vinculado
                      </Label>
                      <p className="text-xs text-gray-400 mb-2">El banner de la carta mostrará el nombre, precio y descripción de este pack automáticamente.</p>
                      <select
                        value={slot.experience_product_id || ''}
                        onChange={(e) => linkExperience(slot.slot_id, e.target.value)}
                        className="w-full px-3 py-2.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-300 outline-none"
                        data-testid={`experience-select-${slot.slot_id}`}
                        disabled={saving}
                      >
                        <option value="">— Sin experiencia —</option>
                        {experiences.map(exp => (
                          <option key={exp.id} value={exp.id}>
                            {exp.name?.es} — {exp.price?.toFixed(2)}€
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Linked Experience Preview */}
                    {linkedExp && (
                      <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                        <div className="flex items-start gap-3">
                          {linkedExp.images?.[0] && (
                            <img src={linkedExp.images[0]} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-amber-900">{linkedExp.name?.es}</p>
                            <p className="text-sm text-amber-700 mt-0.5">{linkedExp.price?.toFixed(2)}€ · {linkedExp.experience_target_people} pers.</p>
                            {linkedExp.experience_copy?.es && (
                              <p className="text-xs text-amber-600 mt-1 italic">"{linkedExp.experience_copy.es}"</p>
                            )}
                            {linkedExp.experience_items?.length > 0 && (
                              <div className="mt-2 space-y-0.5">
                                {linkedExp.experience_items.map((ei, i) => (
                                  <p key={i} className="text-xs text-amber-700">· {ei.es || ei}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          }}
        />
      </div>

      {/* Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <Clock className="text-blue-500 flex-shrink-0" size={20} />
            <p className="text-sm text-blue-700">
              El banner en la carta del cliente muestra automáticamente la experiencia activa según la hora actual. El nombre, descripción y precio se leen directamente del producto vinculado.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MomentoConfigPanel;
